import React, { FC, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  CheckboxIndicator,
  Icon,
  StackItem,
  UtilityText,
} from '@leagueplatform/genesis-core';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  DEFAULT_ENTITY_LIST_STATUSES,
  type Nullable,
} from '@web-config-app/core';
import {
  dropdownMenuTriggerStyles,
  dropdownMenuContentStyles,
} from '../select-action-menu/select-action-menu.styles';

export interface StatusFilterSelectProps {
  currentStatus: Nullable<string>;
  onStatusChange: (status?: string) => void;
}

const STATUS_OPTIONS = [
  { id: 'draft', labelKey: 'DRAFT' },
  { id: 'published', labelKey: 'PUBLISHED' },
  { id: 'archived', labelKey: 'ARCHIVED' },
] as const;

const DEFAULT_STATUSES = new Set<string>(DEFAULT_ENTITY_LIST_STATUSES);

function parseStatuses(value: Nullable<string>): Set<string> {
  if (!value) return new Set(DEFAULT_STATUSES);
  return new Set(value.split(',').filter(Boolean));
}

export const StatusFilterSelect: FC<StatusFilterSelectProps> = ({
  currentStatus,
  onStatusChange,
}) => {
  const { formatMessage } = useIntl();
  const appliedStatuses = parseStatuses(currentStatus);
  const [pendingStatuses, setPendingStatuses] = useState<Set<string>>(
    () => new Set(appliedStatuses),
  );
  const [open, setOpen] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Reset pending to match applied when opening
      setPendingStatuses(new Set(appliedStatuses));
    }
    setOpen(nextOpen);
  };

  const handleToggle = (statusId: string) => {
    setPendingStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) {
        next.delete(statusId);
      } else {
        next.add(statusId);
      }
      return next;
    });
  };

  const handleApply = () => {
    onStatusChange([...pendingStatuses].join(','));
    setOpen(false);
  };

  const activeCount = appliedStatuses.size;

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        <Button
          priority="tertiary"
          size="medium"
          aria-label={formatMessage({ id: 'STATUS' })}
          data-testid="status-filter-trigger"
          css={{
            ...dropdownMenuTriggerStyles,
            height: '$three',
            maxWidth: '9rem',
          }}
        >
          <StackItem
            css={{
              flexDirection: 'row',
              typography: '$bodyTwo',
              color: '$onSurfaceTextPrimary',
            }}
          >
            {`${formatMessage({ id: 'STATUS' })} (${activeCount})`}
            <Icon
              icon="interfaceChevronDown"
              css={{ marginInlineStart: '$half' }}
              size="$oneAndHalf"
              tint="$interactiveActionSubdued"
            />
          </StackItem>
        </Button>
      </DropdownMenu.Trigger>
      <Box
        as={DropdownMenu.Content}
        side="bottom"
        align="start"
        sideOffset={5}
        css={{ ...dropdownMenuContentStyles, width: '11.25rem' }}
        data-testid="status-filter-content"
      >
        {STATUS_OPTIONS.map((opt) => (
          <Box
            as={DropdownMenu.CheckboxItem}
            key={opt.id}
            checked={pendingStatuses.has(opt.id)}
            onCheckedChange={() => handleToggle(opt.id)}
            onSelect={(e: { preventDefault: () => void }) => e.preventDefault()}
            data-testid={`status-filter-${opt.id}`}
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '$half',
              paddingBlock: '$half',
              paddingInline: '$one',
              cursor: 'pointer',
              outline: 'none',
              userSelect: 'none',
              '&[data-highlighted]': {
                backgroundColor: '$surfaceBackgroundSecondary',
              },
            }}
          >
            <CheckboxIndicator
              id={`status-filter-${opt.id}`}
              name="status-filter"
              checked={pendingStatuses.has(opt.id)}
              aria-hidden
            />
            <UtilityText css={{ typography: '$bodyTwo' }}>
              {formatMessage({ id: opt.labelKey })}
            </UtilityText>
          </Box>
        ))}
        <Box
          css={{
            paddingBlock: '$half',
            paddingInline: '$one',
            borderTop:
              'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
          }}
        >
          {pendingStatuses.size === 0 && (
            <UtilityText
              css={{
                color: '$criticalIcon',
                typography: '$caption',
                paddingBlockEnd: '$quarter',
              }}
              data-testid="status-filter-error"
            >
              {formatMessage({ id: 'SELECT_AT_LEAST_ONE_STATUS' })}
            </UtilityText>
          )}
          <DropdownMenu.Item
            asChild
            disabled={pendingStatuses.size === 0}
            onSelect={() => handleApply()}
          >
            <Button
              size="small"
              priority="primary"
              disabled={pendingStatuses.size === 0}
              css={{
                width: '100%',
                justifyContent: 'center',
                paddingInline: '$oneAndHalf',
              }}
              data-testid="status-filter-apply"
            >
              {formatMessage({ id: 'APPLY' })}
            </Button>
          </DropdownMenu.Item>
        </Box>
      </Box>
    </DropdownMenu.Root>
  );
};
